// src/components/Inicio/MisSensores.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { isSuperAdmin } from "../../services/authUtils";
import sensorService from "../../services/sensorService";
import registroSensoresService from "../../services/registroSensoresService";
import TablaRegistrosSensor from "../../components/Registros/TablaRegistrosSensor";

const MisSensores = () => {
  const { user } = useAuth(); // { id, superUsuario, ... }
  const esSuper = useMemo(() => !!isSuperAdmin() || !!user?.superUsuario, [user?.superUsuario]);

  const [data, setData] = useState([]); // estanques -> dispositivos -> sensores (de /sensores/todos)
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  // ====== AUTORREFRESCO (persistente por sesión) ======
  const REFRESH_KEY = "mis-sensores:refreshMs";
  const defaultRefresh = 5 * 60 * 1000; // 5 min
  const [refreshMs, setRefreshMs] = useState(() => {
    const saved = Number(window.localStorage.getItem(REFRESH_KEY));
    return Number.isFinite(saved) ? saved : defaultRefresh;
  });

  useEffect(() => {
    window.localStorage.setItem(REFRESH_KEY, String(refreshMs));
  }, [refreshMs]);

  // Intervalo
  useEffect(() => {
    if (!refreshMs || refreshMs <= 0) return; // Apagado
    const tick = () => {
      cargar();          // lista de sensores (estructura)
      if (esSuper) fetchAdmin(); // tabla consolidada
    };
    const id = setInterval(tick, refreshMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshMs, esSuper]); // si cambian ms o rol, rehacer intervalo

  // ====== SUPERADMIN: estado y paginación ======
  const [adminRows, setAdminRows] = useState([]);
  const [adminTotal, setAdminTotal] = useState(0);
  const [adminPage, setAdminPage] = useState(1);
  const [adminPageSize, setAdminPageSize] = useState(15);
  const [adminQ, setAdminQ] = useState("");
  const [adminUsuarioId, setAdminUsuarioId] = useState("");
  const [adminEstanqueId, setAdminEstanqueId] = useState("");
  const [adminSensorId, setAdminSensorId] = useState("");
  const [adminDesde, setAdminDesde] = useState("");
  const [adminHasta, setAdminHasta] = useState("");
  const [adminBusy, setAdminBusy] = useState(false);
  const adminTotalPages = Math.max(1, Math.ceil(adminTotal / Math.max(1, adminPageSize)));

  // “Boost” sólo para primera carga por conjunto de filtros: 3 páginas
  const firstLoadBoostRef = useRef(true);
  // Cuando cambian filtros “lógicos”, reseteamos el boost y la página
  useEffect(() => {
    firstLoadBoostRef.current = true;
    setAdminPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminQ, adminUsuarioId, adminEstanqueId, adminSensorId, adminDesde, adminHasta]);

  const cargar = async () => {
    setCargando(true);
    setError("");
    try {
      const resp = await sensorService.obtenerTodosSensores();
      setData(resp || []);
    } catch (e) {
      console.error(e);
      setError("No se pudo cargar la lista de sensores.");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  // Lista plana de sensores accesibles (para autocompletar SensorId en superadmin)
  const sensoresAccesibles = useMemo(() => {
    const flat = [];
    (data || []).forEach(e => {
      (e.dispositivos || []).forEach(d => {
        (d.sensores || []).forEach(s => {
          flat.push({
            id: s.id,
            nombre: s.nombre,
            unidad: s.unidad,
            tipoParametroNombre: s.tipoParametroNombre,
            dispositivoNombre: d.dispositivoNombre,
            estanqueNombre: e.estanqueNombre,
          });
        });
      });
    });
    // Evitar duplicados por si llegan repetidos
    const seen = new Set();
    return flat.filter(s => {
      if (seen.has(s.id)) return false;
      seen.add(s.id);
      return true;
    });
  }, [data]);

  // ---- Superadmin consolidated fetch ----
  const fetchAdmin = async () => {
    setAdminBusy(true);
    try {
      const effectivePageSize = firstLoadBoostRef.current ? adminPageSize * 3 : adminPageSize;
      const payload = {
        usuarioId: adminUsuarioId ? parseInt(adminUsuarioId,10) : null,
        estanqueId: adminEstanqueId ? parseInt(adminEstanqueId,10) : null,
        sensorId: adminSensorId ? parseInt(adminSensorId,10) : null,
        q: adminQ || "",
        desdeUtc: adminDesde ? new Date(adminDesde).toISOString() : null,
        hastaUtc: adminHasta ? new Date(adminHasta).toISOString() : null,
        page: adminPage,
        pageSize: effectivePageSize,
        desc: true, // asegurar descendente desde backend
      };
      const r = await registroSensoresService.buscarAdmin(payload);
      // Seguridad extra: ordenar descendente por fecha en cliente también
      const items = (r.items || []).slice().sort((a, b) => {
        const ta = new Date(a.fechaRegistroUtc).getTime();
        const tb = new Date(b.fechaRegistroUtc).getTime();
        return tb - ta;
      });
      setAdminRows(items);
      setAdminTotal(r.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      firstLoadBoostRef.current = false; // después de la primera carga, desactivar boost
      setAdminBusy(false);
    }
  };

  useEffect(() => {
    if (esSuper) fetchAdmin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esSuper, adminPage, adminPageSize, adminQ, adminUsuarioId, adminEstanqueId, adminSensorId, adminDesde, adminHasta]);

  // ===================== EXPORT/PRINT (panel y funciones estilo ListaSensores) =====================
  const columnsDef = [
    { key: "fecha", label: "Fecha (UTC)" },
    { key: "valor", label: "Valor" },
    { key: "unidad", label: "Unidad" },
    { key: "tipo", label: "Tipo" },
    { key: "sensor", label: "Sensor" },
    { key: "dispositivo", label: "Dispositivo" },
    { key: "estanque", label: "Estanque" },
  ];

  const [showExport, setShowExport] = useState(false);
  const [exportFormat, setExportFormat] = useState("csv"); // "csv" | "xls" | "pdf"
  const [colsToExport, setColsToExport] = useState({
    fecha: true, valor: true, unidad: true, tipo: true, sensor: true, dispositivo: true, estanque: true,
  });
  const toggleColExport = (k) => setColsToExport((p) => ({ ...p, [k]: !p[k] }));

  // helpers
  const escapeHtml = (s) =>
    String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");

  const escapeCsv = (val) => {
    const s = String(val ?? "");
    const needsQuotes = /[",\r\n;]/.test(s);
    const escaped = s.replace(/"/g, '""');
    return needsQuotes ? `"${escaped}"` : escaped;
  };

  const buildTheadHtml = (cols) => `<tr>${cols.map((c) => `<th>${c.label}</th>`).join("")}</tr>`;

  const buildRowTdsHtml = (r, colsMap) => {
    const tds = [];
    if (colsMap.fecha) tds.push(`<td>${escapeHtml(new Date(r.fechaRegistroUtc).toISOString())}</td>`);
    if (colsMap.valor) tds.push(`<td style="text-align:right">${escapeHtml(r.valor)}</td>`);
    if (colsMap.unidad) tds.push(`<td>${escapeHtml(r.unidad ?? "")}</td>`);
    if (colsMap.tipo) tds.push(`<td>${escapeHtml(r.tipoParametroNombre ?? "")}</td>`);
    if (colsMap.sensor) tds.push(`<td>${escapeHtml(r.sensorNombre ?? "")}</td>`);
    if (colsMap.dispositivo) tds.push(`<td>${escapeHtml(r.dispositivoNombre ?? "")}</td>`);
    if (colsMap.estanque) tds.push(`<td>${escapeHtml(r.estanqueNombre ?? "")}</td>`);
    return tds.join("");
  };

  const buildTableHtml = (colsMap) => {
    const cols = columnsDef.filter((c) => colsMap[c.key]);
    const thead = buildTheadHtml(cols);
    const htmlRows = adminRows.map((r) => `<tr>${buildRowTdsHtml(r, colsMap)}</tr>`).join("");
    return { thead, htmlRows, cols };
  };

  const buildPrintHtml = (colsMap, title = "Registros (Superadministrador)") => {
    const { thead, htmlRows } = buildTableHtml(colsMap);
    const now = new Date().toLocaleString();
    return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  @media print { @page { size: auto; margin: 12mm; } }
  body { font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; color:#111827; }
  h1 { font-size:20px; margin:0 0 4px 0; }
  .sub { color:#6b7280; font-size:12px; margin-bottom:12px; }
  table { border-collapse: collapse; width: 100%; font-size:12px; }
  th, td { border:1px solid #0ea5a3; padding:8px; vertical-align: top; }
  thead th { background:#111827; color:#67e8f9; }
  tr:nth-child(even) td { background:#f8fafc; }
  td:nth-child(2) { text-align:right; }
</style>
</head>
<body>
  <h1>${escapeHtml(title)}</h1>
  <div class="sub">Generado: ${escapeHtml(now)} • Mostrando: ${adminRows.length} de ${adminTotal}</div>
  <table>
    <thead>${thead}</thead>
    <tbody>${htmlRows}</tbody>
  </table>
  <script>window.onload = () => { window.print(); setTimeout(() => window.close(), 300); };</script>
</body>
</html>`;
  };

  const triggerDownload = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // acciones export
  const handleExport = () => {
    if (exportFormat === "csv") return exportCsv();
    if (exportFormat === "xls") return exportXls();
    if (exportFormat === "pdf") return exportPdf();
  };

  const exportCsv = () => {
    const headers = columnsDef.filter((h) => colsToExport[h.key]);
    const headerRow = headers.map((h) => escapeCsv(h.label)).join(",");

    const dataRows = adminRows.map((r) => {
      const vals = [];
      if (colsToExport.fecha) vals.push(escapeCsv(new Date(r.fechaRegistroUtc).toISOString()));
      if (colsToExport.valor) vals.push(escapeCsv(String(r.valor)));
      if (colsToExport.unidad) vals.push(escapeCsv(r.unidad ?? ""));
      if (colsToExport.tipo) vals.push(escapeCsv(r.tipoParametroNombre ?? ""));
      if (colsToExport.sensor) vals.push(escapeCsv(r.sensorNombre ?? ""));
      if (colsToExport.dispositivo) vals.push(escapeCsv(r.dispositivoNombre ?? ""));
      if (colsToExport.estanque) vals.push(escapeCsv(r.estanqueNombre ?? ""));
      return vals.join(",");
    });

    const csv = [headerRow, ...dataRows].join("\r\n");
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const iso = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    triggerDownload(url, `registros-superadmin-${iso}.csv`);
  };

  const exportXls = () => {
    const { thead, htmlRows } = buildTableHtml(colsToExport);
    const now = new Date().toLocaleString();
    const title = "Registros (Superadministrador)";
    const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8" />
<!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
<x:Name>${escapeHtml(title)}</x:Name>
<x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
</x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
  <table border="1" style="border-collapse:collapse;">
    <thead style="background:#111827;color:#67e8f9;font-weight:bold;">${thead}</thead>
    <tbody>${htmlRows}</tbody>
  </table>
  <div>Generado: ${escapeHtml(now)} • Mostrando: ${adminRows.length} de ${adminTotal}</div>
</body>
</html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const iso = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    triggerDownload(url, `registros-superadmin-${iso}.xls`);
  };

  const exportPdf = () => {
    const html = buildPrintHtml(colsToExport, "Registros (Superadministrador)");
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.open();
    w.document.write(html);
    w.document.close();
  };
  // =================================================================================================

  // ---- UI ----
  return (
    <div className="bg-neutral-900 text-white rounded-lg p-4 sm:p-6 w-full max-w-7xl mx-auto">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-xl sm:text-2xl font-bold text-green-400">Mis Sensores</h2>
        <div className="flex items-center gap-3">
          {/* Selector de auto-actualización */}
          <label className="text-xs sm:text-sm flex items-center gap-2">
            <span className="text-neutral-300">Actualizar cada:</span>
            <select
              value={refreshMs}
              onChange={(e) => setRefreshMs(Number(e.target.value))}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs sm:text-sm"
              title="Selecciona cada cuánto se refresca esta pestaña"
            >
              <option value={0}>Apagado</option>
              <option value={30_000}>30 s</option>
              <option value={60_000}>1 min</option>
              <option value={120_000}>2 min</option>
              <option value={300_000}>5 min</option>
              <option value={600_000}>10 min</option>
            </select>
          </label>

          {esSuper && (
            <span className="text-xs border border-emerald-500/60 px-2 py-1 rounded text-emerald-300">
              Superadministrador
            </span>
          )}
        </div>
      </div>

      {cargando && <div className="mt-4 text-slate-300">Cargando…</div>}
      {!cargando && error && <div className="mt-4 text-red-400">{error}</div>}

      {/* SUPERADMIN: tabla única con filtros */}
      {esSuper && (
        <div className="mt-4 rounded border border-slate-800 p-3 bg-slate-900">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <input
                value={adminUsuarioId}
                onChange={e=>{setAdminUsuarioId(e.target.value)}}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
                placeholder="UsuarioId"
              />
              <input
                value={adminEstanqueId}
                onChange={e=>{setAdminEstanqueId(e.target.value)}}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
                placeholder="EstanqueId"
              />

              {/* SensorId con sugerencias desde sensoresAccesibles */}
              <input
                list="sensorList"
                value={adminSensorId}
                onChange={e=>{ setAdminSensorId(e.target.value); }}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
                placeholder="SensorId (escribe o elige)"
              />
              <datalist id="sensorList">
                {sensoresAccesibles.map(s => (
                  <option
                    key={s.id}
                    value={s.id}
                    label={`${s.estanqueNombre} / ${s.dispositivoNombre} / ${s.nombre}`}
                  >
                    {`${s.estanqueNombre} / ${s.dispositivoNombre} / ${s.nombre}`}
                  </option>
                ))}
              </datalist>

              <input
                value={adminQ}
                onChange={e=>{setAdminQ(e.target.value)}}
                className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm col-span-2 md:col-span-3"
                placeholder="Buscar por estanque/dispositivo/sensor/tipo…"
              />
              <div className="flex items-center gap-2 col-span-2 md:col-span-3">
                <input
                  type="datetime-local"
                  value={adminDesde}
                  onChange={e=>{setAdminDesde(e.target.value)}}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
                />
                <span className="text-slate-400 text-sm">→</span>
                <input
                  type="datetime-local"
                  value={adminHasta}
                  onChange={e=>{setAdminHasta(e.target.value)}}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
                />
                <button
                  onClick={()=>{setAdminDesde("");setAdminHasta("");}}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm"
                >
                  Limpiar
                </button>
              </div>
            </div>

            {/* Acciones */}
            <div className="flex items-center gap-2">
              <button onClick={() => {
                const html = buildPrintHtml(colsToExport, "Registros (Superadministrador)");
                const w = window.open("", "_blank"); if(!w) return;
                w.document.open(); w.document.write(html); w.document.close();
              }} className="bg-cyan-700 hover:bg-cyan-600 text-white rounded px-3 py-1 text-sm">Imprimir</button>

              {/* Exportar → muestra opciones como en ListaSensores */}
              <button onClick={() => setShowExport(s => !s)} className="bg-emerald-700 hover:bg-emerald-600 text-white rounded px-3 py-1 text-sm">
                Exportar
              </button>
            </div>
          </div>

          {/* Panel de exportación */}
          {showExport && (
            <div className="mt-3 p-3 rounded border border-slate-700 bg-slate-800">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm">Formato:</span>
                  <select
                    value={exportFormat}
                    onChange={(e) => setExportFormat(e.target.value)}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm"
                  >
                    <option value="csv">CSV</option>
                    <option value="xls">Excel (HTML)</option>
                    <option value="pdf">PDF (imprimir)</option>
                  </select>
                </div>
                <button
                  onClick={handleExport}
                  className="bg-emerald-700 hover:bg-emerald-600 text-white px-3 py-1 rounded text-sm"
                >
                  Exportar ahora
                </button>
              </div>

              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                {Object.entries(colsToExport).map(([k, v]) => (
                  <label key={k} className="flex items-center gap-2">
                    <input type="checkbox" checked={v} onChange={() => toggleColExport(k)} />
                    <span>{columnsDef.find((c) => c.key === k)?.label ?? k}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 overflow-auto rounded border border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800 text-cyan-200">
                  <th className="px-2 py-2 text-left">Fecha (UTC)</th>
                  <th className="px-2 py-2 text-right">Valor</th>
                  <th className="px-2 py-2 text-left">Unidad</th>
                  <th className="px-2 py-2 text-left">Tipo</th>
                  <th className="px-2 py-2 text-left">Sensor</th>
                  <th className="px-2 py-2 text-left">Dispositivo</th>
                  <th className="px-2 py-2 text-left">Estanque</th>
                </tr>
              </thead>
              <tbody>
                {adminBusy && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-300">Cargando…</td></tr>}
                {!adminBusy && adminRows.length === 0 && <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-400">Sin resultados.</td></tr>}
                {!adminBusy && adminRows.map((r) => (
                  <tr key={r.id} className="odd:bg-slate-900 even:bg-slate-950">
                    <td className="px-2 py-2">{new Date(r.fechaRegistroUtc).toISOString()}</td>
                    <td className="px-2 py-2 text-right">{r.valor}</td>
                    <td className="px-2 py-2">{r.unidad ?? ""}</td>
                    <td className="px-2 py-2">{r.tipoParametroNombre}</td>
                    <td className="px-2 py-2">{r.sensorNombre}</td>
                    <td className="px-2 py-2">{r.dispositivoNombre}</td>
                    <td className="px-2 py-2">{r.estanqueNombre}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* paginación */}
          <div className="mt-3 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <span>Página {adminPage} de {adminTotalPages}</span>
              <button
                disabled={adminPage<=1}
                onClick={()=>setAdminPage(p=>Math.max(1,p-1))}
                className={`px-2 py-1 rounded border ${adminPage<=1 ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-800"}`}
              >
                ←
              </button>
              <button
                disabled={adminPage>=adminTotalPages}
                onClick={()=>setAdminPage(p=>p+1)}
                className={`px-2 py-1 rounded border ${adminPage>=adminTotalPages ? "opacity-40 cursor-not-allowed" : "hover:bg-slate-800"}`}
              >
                →
              </button>
            </div>
            <div className="flex items-center gap-2">
              <span>Registros por página:</span>
              <select
                value={adminPageSize}
                onChange={e=>{setAdminPageSize(parseInt(e.target.value,10)||15); setAdminPage(1); firstLoadBoostRef.current=true;}}
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1"
              >
                {[10,15,20,30,50,100,200,500].map(n=><option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* NO SUPER: Agrupado por estanque, tablas por sensor apiladas */}
      {!esSuper && (
        <div className="mt-4">
          {(data || []).map((e) => {
            const sensores = (e.dispositivos || []).flatMap(d =>
              (d.sensores || []).map(s => ({
                id: s.id,
                nombre: s.nombre,
                unidad: s.unidad,
                tipoParametroNombre: s.tipoParametroNombre,
                dispositivoNombre: d.dispositivoNombre,
                estanqueNombre: e.estanqueNombre,
              }))
            );

            if (sensores.length === 0) return null;

            return (
              <div key={e.estanqueNombre} className="mb-6">
                <div className="text-lg font-semibold text-cyan-300">{e.estanqueNombre}</div>
                <div className="mt-2">
                  {sensores.map(s => (
                    <TablaRegistrosSensor key={s.id} sensor={s} defaultPageSize={15}/>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MisSensores;
